import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ListOrdered,
  Phone,
  Activity,
  MessageCircle,
  BarChart3,
  ShieldCheck,
  Building2,
  Zap,
  ArrowRight,
} from 'lucide-react';
import styles from './HomePage.module.css';

/* Canonical production origin for SEO tags. The marketing landing is served at
   the app root; change this if Relabix gets a dedicated marketing domain. */
const SITE_ORIGIN = 'https://crm.nicedialtech.com';

/** What the product actually does, drawn from the CRM's real modules. */
const FEATURES = [
  {
    icon: Users,
    title: 'Lead management',
    body: 'Capture, track and work every lead in one place — with ownership, dispositions and a full activity trail.',
  },
  {
    icon: ListOrdered,
    title: 'Smart allocation & queues',
    body: 'Distribute leads automatically by queue, priority and rules, so agents always work the right lead next.',
  },
  {
    icon: Phone,
    title: 'Built-in dialer',
    body: 'Click-to-call from a browser softphone — hold, mute and disposition without a separate dialer app.',
  },
  {
    icon: Activity,
    title: 'Real-time monitoring',
    body: 'See live agent status, active calls and break tracking across your floor as it happens.',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp & SMS',
    body: 'Reach leads on WhatsApp and SMS through WABA Talk, right from the lead — no copy-pasting numbers.',
  },
  {
    icon: BarChart3,
    title: 'Reports & insights',
    body: 'Allocation, call and disposition reports that export in a click, so managers see what is working.',
  },
];

const TRUST = [
  {
    icon: Building2,
    title: 'Multi-tenant',
    body: 'Run many organisations on one platform, each with its own users, data and settings.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure by design',
    body: 'Role-based access, per-organisation IP restrictions and encrypted sessions, out of the box.',
  },
  {
    icon: Zap,
    title: 'Fast to adopt',
    body: 'A clean, modern interface your agents understand on day one — no lengthy training needed.',
  },
];

export default function HomePage() {
  const navigate = useNavigate();

  /* Lightweight per-page SEO (CRA SPA, no head library). The static index.html
     carries the defaults; this keeps the title/description correct on the home
     route and after client navigation. */
  useEffect(() => {
    const title = 'Relabix — CRM for calling and sales teams';
    const description =
      'Relabix is a multi-tenant CRM for calling and sales teams: lead management, smart allocation, a built-in dialer, real-time monitoring, WhatsApp & SMS, and reports.';
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', `${SITE_ORIGIN}/`);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
  }, []);

  const goSignIn = () => navigate('/login');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">R</span>
          <span>Relabix</span>
        </div>
        <div className={styles.navActions}>
          <button type="button" className={styles.btnPrimary} onClick={goSignIn}>
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <span className={styles.eyebrow}>
          <Zap size={14} /> The CRM for calling &amp; sales teams
        </span>
        <h1 className={styles.heroTitle}>
          Run your calling floor on <span className={styles.accent}>one platform</span>
        </h1>
        <p className={styles.heroSub}>
          Relabix brings leads, dialing, WhatsApp, SMS and real-time monitoring together —
          so your team spends time selling, not switching tabs.
        </p>
        <div className={styles.heroActions}>
          <button type="button" className={`${styles.btnPrimary} ${styles.btnLg}`} onClick={goSignIn}>
            Sign in <ArrowRight size={18} />
          </button>
          <a className={`${styles.btnGhost} ${styles.btnLg}`} href="#features">
            See what it does
          </a>
        </div>
        <p className={styles.heroNote}>Already have an account? Sign in with your user code.</p>
      </section>

      {/* Features */}
      <section className={styles.section} id="features">
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Everything your floor needs</h2>
          <p className={styles.sectionSub}>
            From the first lead to the closed deal — the tools your agents and managers use every day.
          </p>
        </div>
        <div className={styles.grid}>
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div className={styles.card} key={title}>
              <span className={styles.cardIcon}><Icon size={22} /></span>
              <h3 className={styles.cardTitle}>{title}</h3>
              <p className={styles.cardBody}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className={styles.trust}>
        <div className={styles.section}>
          <div className={styles.trustGrid}>
            {TRUST.map(({ icon: Icon, title, body }) => (
              <div className={styles.trustItem} key={title}>
                <Icon size={22} />
                <div>
                  <strong>{title}</strong>
                  <span>{body}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2>Ready to get your team moving?</h2>
          <p>Sign in to Relabix and put every lead, call and conversation in one place.</p>
          <button type="button" className={`${styles.btnPrimary} ${styles.btnLg} ${styles.btnOnDark}`} onClick={goSignIn}>
            Sign in <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.brand} style={{ fontSize: '1rem' }}>
          <span className={styles.brandMark} aria-hidden="true">R</span>
          <span>Relabix</span>
        </div>
        <span>&copy; {new Date().getFullYear()} Relabix. All rights reserved.</span>
      </footer>
    </div>
  );
}
